import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Layout from '../components/layouts/Main';
import config from '../config';

const Packages = () => {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingPackage, setEditingPackage] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const modalRef = useRef(null);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        duration: '',
        modules: [],
        maxUsers: '',
        maxProducts: '',
        features: [],
        isActive: true
    });

    useEffect(() => {
        fetchPackages();
    }, []);

    const fetchPackages = async () => {
        try {
            const response = await axios.get(`${config.API_BASE_URL}/api/master/packages`);
            setPackages(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching packages:', error);
            toast.error('Failed to fetch packages');
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const submitData = {
                ...formData,
                modules: formData.modules.filter(m => m.trim() !== ''),
                features: formData.features.filter(f => f.trim() !== ''),
                price: parseFloat(formData.price),
                duration: parseInt(formData.duration),
                maxUsers: parseInt(formData.maxUsers),
                maxProducts: parseInt(formData.maxProducts)
            };

            if (editingPackage) {
                await axios.put(`${config.API_BASE_URL}/api/master/packages/${editingPackage._id}`, submitData);
                toast.success('Package updated successfully!');
            } else {
                await axios.post(`${config.API_BASE_URL}/api/master/packages`, submitData);
                toast.success('Package created successfully!');
            }

            fetchPackages();
            closeModal();
        } catch (error) {
            console.error('Error saving package:', error);
            toast.error('Failed to save package');
        }
    };

    const handleEdit = (pkg) => {
        setEditingPackage(pkg);
        setFormData({
            name: pkg.name,
            description: pkg.description,
            price: pkg.price,
            duration: pkg.duration,
            modules: pkg.modules || [],
            maxUsers: pkg.maxUsers,
            maxProducts: pkg.maxProducts,
            features: pkg.features || [],
            isActive: pkg.isActive
        });
        setShowModal(true);
    };

    const handleDelete = async (pkgId) => {
        if (window.confirm('Are you sure you want to delete this package?')) {
            try {
                await axios.delete(`${config.API_BASE_URL}/api/master/packages/${pkgId}`);
                toast.success('Package deleted successfully!');
                fetchPackages();
            } catch (error) {
                console.error('Error deleting package:', error);
                toast.error('Failed to delete package');
            }
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingPackage(null);
        setFormData({
            name: '',
            description: '',
            price: '',
            duration: '',
            modules: [],
            maxUsers: '',
            maxProducts: '',
            features: [],
            isActive: true
        });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleArrayChange = (field, value, index) => {
        const newArray = [...formData[field]];
        newArray[index] = value;
        setFormData(prev => ({
            ...prev,
            [field]: newArray
        }));
    };

    const addArrayItem = (field) => {
        setFormData(prev => ({
            ...prev,
            [field]: [...prev[field], '']
        }));
    };

    const removeArrayItem = (field, index) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].filter((_, i) => i !== index)
        }));
    };

    return (
        <Layout>
            <div>
                <nav className="mb-3" aria-label="breadcrumb">
                    <ol className="breadcrumb mb-0">
                        <li className="breadcrumb-item">
                            <a href="#!">Pages</a>
                        </li>
                        <li className="breadcrumb-item active">Packages</li>
                    </ol>
                </nav>
                <h2 className="text-bold text-body-emphasis mb-5">Package Management</h2>

                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div></div>
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowModal(true)}
                    >
                        <span className="fas fa-plus me-2" />
                        Add Package
                    </button>
                </div>

                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className="row">
                        {packages.map(pkg => (
                            <div key={pkg._id} className="col-md-6 col-lg-4 mb-4">
                                <div className="card h-100">
                                    <div className="card-body">
                                        <h5 className="card-title">{pkg.name}</h5>
                                        <p className="card-text text-muted">{pkg.description}</p>

                                        <div className="mb-3">
                                            <span className="badge bg-primary me-2">${pkg.price}</span>
                                            <span className="badge bg-secondary">{pkg.duration} days</span>
                                        </div>

                                        <div className="mb-2">
                                            <small className="text-muted">
                                                <strong>Max Users:</strong> {pkg.maxUsers} | <strong>Max Products:</strong> {pkg.maxProducts}
                                            </small>
                                        </div>

                                        {pkg.modules && pkg.modules.length > 0 && (
                                            <div className="mb-2">
                                                <small className="text-muted">
                                                    <strong>Modules:</strong> {pkg.modules.join(', ')}
                                                </small>
                                            </div>
                                        )}

                                        <div className="d-flex justify-content-between align-items-center">
                                            <span className={`badge ${pkg.isActive ? 'bg-success' : 'bg-danger'}`}>
                                                {pkg.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                            <div>
                                                <button
                                                    className="btn btn-sm btn-outline-primary me-2"
                                                    onClick={() => handleEdit(pkg)}
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDelete(pkg._id)}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Modal for Add/Edit Package */}
                <div className={`modal fade ${showModal ? 'show' : ''}`} style={{ display: showModal ? 'block' : 'none' }} ref={modalRef}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editingPackage ? 'Edit Package' : 'Add New Package'}
                                </h5>
                                <button type="button" className="btn-close" onClick={closeModal}></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Package Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Price ($)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className="form-control"
                                                name="price"
                                                value={formData.price}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Duration (days)</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="duration"
                                                value={formData.duration}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Max Users</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="maxUsers"
                                                value={formData.maxUsers}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Max Products</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="maxProducts"
                                                value={formData.maxProducts}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-12 mb-3">
                                            <label className="form-label">Description</label>
                                            <textarea
                                                className="form-control"
                                                name="description"
                                                value={formData.description}
                                                onChange={handleChange}
                                                rows="3"
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Modules</label>
                                        {formData.modules.map((module, index) => (
                                            <div key={index} className="input-group mb-2">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={module}
                                                    onChange={(e) => handleArrayChange('modules', e.target.value, index)}
                                                    placeholder="Enter module name"
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-danger"
                                                    onClick={() => removeArrayItem('modules', index)}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={() => addArrayItem('modules')}
                                        >
                                            <i className="fas fa-plus me-1"></i> Add Module
                                        </button>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Features</label>
                                        {formData.features.map((feature, index) => (
                                            <div key={index} className="input-group mb-2">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={feature}
                                                    onChange={(e) => handleArrayChange('features', e.target.value, index)}
                                                    placeholder="Enter feature name"
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-danger"
                                                    onClick={() => removeArrayItem('features', index)}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={() => addArrayItem('features')}
                                        >
                                            <i className="fas fa-plus me-1"></i> Add Feature
                                        </button>
                                    </div>

                                    <div className="mb-3">
                                        <div className="form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                name="isActive"
                                                checked={formData.isActive}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label">Active</label>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeModal}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        {editingPackage ? 'Update Package' : 'Create Package'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Packages;
